import {
  ArrowDown,
  ArrowRight,
  ClipboardList,
  Download,
  Layers3,
  MessageCircle,
  Palette,
  Ruler,
  ShieldCheck,
  Warehouse,
  Wrench,
} from "lucide-react";
import { PHOTOS } from "@/lib/site";

const categories = [
  {
    icon: Warehouse,
    title: "Profil Atap GL & PPGL",
    desc: "Trimdeck, Spandeck, Socca, Convey, Ecostrim, Trimdeck Lengkung, Fiber Bening, serta Atap Pasir sesuai kebutuhan proyek.",
  },
  {
    icon: ShieldCheck,
    title: "Seri Genteng Metal",
    desc: "Genteng Metal Oval, Kotak Minimalis, Oval Plus, dan Genteng Bambu dengan pilihan ketebalan serta panjang sesuai pesanan.",
  },
  {
    icon: Layers3,
    title: "Profil Struktur & Baja Ringan",
    desc: "Bondeck galvanis, Canal C Galvalum, Reng Galvalum, dan Hollow Galvalum untuk kebutuhan struktur atap maupun bangunan.",
  },
  {
    icon: Wrench,
    title: "Aksesoris & Material Pendukung",
    desc: "Nok Bulat, Nok V300, talang rol, foil insulasi, sekrup bor, stainless steel sheet, galvanis sheet, slitted coil, dan material baja lainnya.",
  },
];

const advantages = [
  { icon: Palette, label: "Beragam profil dan pilihan warna" },
  { icon: Ruler, label: "Ketebalan serta panjang sesuai kebutuhan" },
  { icon: Layers3, label: "Tersedia material GL & PPGL" },
  { icon: MessageCircle, label: "Konsultasi kebutuhan proyek dengan tim sales" },
];

const projectPhotos = [
  {
    title: "Proyek atap biru",
    desc: "Aplikasi atap galvalum untuk bangunan operasional.",
    image: PHOTOS.galvalumProjectBlueRoof,
  },
  {
    title: "Area gudang dan struktur",
    desc: "Referensi penggunaan roofing dan struktur pendukung.",
    image: PHOTOS.galvalumProjectBuilding,
  },
  {
    title: "Ceiling biru galvalum",
    desc: "Aplikasi material galvalum sebagai elemen plafon dan finishing interior.",
    image: PHOTOS.galvalumProjectBlueCeiling,
  },
  {
    title: "Genteng metal hunian",
    desc: "Referensi genteng metal untuk bangunan hunian dan fasilitas pendukung.",
    image: PHOTOS.galvalumProjectMetalRoof,
  },
  {
    title: "Struktur baja ringan",
    desc: "Tahap pemasangan struktur ringan dan rangka atap untuk proyek bangunan.",
    image: PHOTOS.galvalumProjectStructure,
  },
  {
    title: "Struktur kandang galvalum",
    desc: "Aplikasi baja ringan dan galvalum untuk area kandang dan penutup atap.",
    image: PHOTOS.galvalumProjectSheepStructure,
  },
];

export const GalvalumPage = () => {
  return (
    <>
      <section
        id="produk-detail"
        data-testid="galvalum-hero"
        className="relative min-h-screen pt-36 pb-24 overflow-hidden bg-animax-black text-white"
      >
        <div className="absolute inset-0">
          <img
            src={PHOTOS.galvalumWarehouse}
            alt="Gudang material galvalum"
            className="w-full h-full object-cover opacity-70"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-animax-black via-animax-black/78 to-animax-black/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-animax-black via-transparent to-animax-black/55" />
          <div className="absolute inset-0 bg-blue-900/10" />
        </div>

        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/20 bg-white/10 backdrop-blur-md mb-6">
              <Warehouse className="w-4 h-4 text-animax-yellow" />
              <span className="tag-uppercase text-white/80">Baja Ringan & Galvalum</span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl leading-[1.05] tracking-tighter font-medium">
              Baja Ringan, Galvalum & Roofing untuk Berbagai Kebutuhan Proyek
            </h1>
            <p className="mt-6 text-base sm:text-lg text-white/72 leading-relaxed max-w-2xl">
              Pilihan produk atap, struktur ringan, aksesoris, dan material pendukung untuk
              hunian, ruko, gudang, hingga bangunan industri.
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href="#kategori-galvalum"
                className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-animax-yellow text-animax-black font-medium text-sm hover:bg-animax-orange hover:text-white transition-all"
              >
                <ClipboardList className="w-4 h-4" />
                Lihat Katalog Produk
                <ArrowDown className="w-4 h-4" />
              </a>
              <a
                href="#tim-sales"
                className="inline-flex items-center gap-2 px-7 py-4 rounded-full border border-white/45 text-white font-medium text-sm hover:bg-white hover:text-animax-black transition-all"
              >
                <MessageCircle className="w-4 h-4" />
                Hubungi Tim Sales
              </a>
            </div>
          </div>
        </div>
      </section>

      <section id="kategori-galvalum" className="py-24 lg:py-32 bg-white" data-testid="galvalum-categories">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl">
            <div className="tag-uppercase text-animax-orange mb-4">Kategori Produk</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium text-animax-black">
              Kategori utama sesuai katalog Baja Ringan & Galvalum
            </h2>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
            {categories.map((item, index) => (
              <article key={item.title} className="card-lift p-6 rounded-2xl border border-neutral-200 bg-white">
                <div className="w-12 h-12 rounded-xl bg-animax-yellow/20 flex items-center justify-center mb-5">
                  <item.icon className="w-5 h-5 text-animax-orange" />
                </div>
                <h3 className="font-display text-2xl font-medium text-animax-black">{item.title}</h3>
                <p className="mt-3 text-sm text-neutral-600 leading-relaxed">{item.desc}</p>
                <a
                  href="#tim-sales"
                  data-testid={`galvalum-category-${index}`}
                  className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-animax-black hover:text-animax-orange"
                >
                  Tanya Produk
                  <ArrowRight className="w-4 h-4" />
                </a>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-animax-gray" data-testid="galvalum-advantages">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {advantages.map((item) => (
              <div key={item.label} className="p-5 rounded-2xl border border-neutral-200 bg-white flex gap-4">
                <div className="w-10 h-10 rounded-lg bg-animax-yellow/20 flex items-center justify-center shrink-0">
                  <item.icon className="w-5 h-5 text-animax-orange" />
                </div>
                <div className="text-sm font-medium text-animax-black leading-snug">{item.label}</div>
              </div>
            ))}
          </div>
          <p className="mt-5 text-xs text-neutral-500 leading-relaxed">
            Ketersediaan produk, warna, ukuran, dan spesifikasi mengikuti stok maupun pesanan.
          </p>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-white" data-testid="galvalum-catalog">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6">
              <div className="tag-uppercase text-animax-orange mb-4">Katalog Produk</div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium text-animax-black">
                Butuh Spesifikasi Lengkap?
              </h2>
              <p className="mt-4 text-neutral-600 text-lg leading-relaxed">
                Lihat pilihan profil atap, genteng metal, struktur ringan, aksesoris, dan material
                pendukung pada katalog produk kami.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a href="#kategori-galvalum" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-animax-black text-white text-sm font-medium hover:bg-animax-orange transition-all">
                  Lihat Katalog Lengkap
                  <ArrowRight className="w-4 h-4" />
                </a>
                <a
                  href="/catalogs/katalog-produk-gl-apl-25.pdf"
                  download
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-animax-black text-animax-black text-sm font-medium hover:bg-animax-black hover:text-white transition-all"
                >
                  <Download className="w-4 h-4" />
                  Download Katalog PDF
                </a>
              </div>
            </div>

            <div className="lg:col-span-6 rounded-2xl overflow-hidden border border-neutral-200 aspect-[16/11]">
              <img src={PHOTOS.galvalumWarehouse} alt="Material baja ringan dan galvalum" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-animax-black text-white" data-testid="galvalum-projects">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl">
            <div className="tag-uppercase text-animax-yellow mb-4">Hasil Proyek</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium">
              Galeri foto aplikasi Baja Ringan & Galvalum di lapangan
            </h2>
            <p className="mt-4 text-white/65 text-lg leading-relaxed">
              Section ini disiapkan khusus untuk foto proyek atap, gudang, bangunan, dan aplikasi
              material yang sudah digunakan pelanggan.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {projectPhotos.map((photo) => (
              <article key={photo.title} className="relative aspect-[16/11] rounded-2xl overflow-hidden border border-white/10">
                <img src={photo.image} alt={photo.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-animax-black/85 via-transparent to-transparent" />
                <div className="absolute left-6 right-6 bottom-6">
                  <h3 className="font-display text-2xl font-medium">{photo.title}</h3>
                  <p className="mt-2 text-sm text-white/65">{photo.desc}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-animax-yellow text-animax-black" data-testid="galvalum-closing-cta">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <div className="tag-uppercase text-animax-black/60 mb-4">Penawaran Proyek</div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium">
                Butuh Material untuk Proyek Anda?
              </h2>
              <p className="mt-4 text-animax-black/70 text-lg leading-relaxed">
                Hubungi tim PT Animax Putra Lancar untuk konsultasi produk, ukuran, ketebalan, dan
                kebutuhan pengiriman.
              </p>
            </div>
            <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3">
              <a href="#tim-sales" className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-animax-black text-white font-medium text-sm hover:bg-white hover:text-animax-black transition-all">
                <MessageCircle className="w-4 h-4" />
                Hubungi Tim Sales
              </a>
              <a href="#kontak" className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full border border-animax-black text-animax-black font-medium text-sm hover:bg-animax-black hover:text-white transition-all">
                Minta Penawaran
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};
